/**
 * Boomfection Landing — Leaderboard Module
 * Season selector, seasonal/global/map leaderboards
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { escapeHtml } from '/js/ui/utils.js';
import { safeJsonFetch } from './LandingUtils.js';
import { t } from '/js/i18n.js';

let currentSelectedSeason = 'current';
let currentLeaderboardCategory = 'wins';
let extendedLeaderboardsInitialized = false;
let leaderboardObserver = null;

/**
 * Initializes the season dropdown and tab navigation
 */
export async function initSeasonSelector() {
    const select = document.getElementById('season-select');
    if (!select) return;

    try {
        const data = await safeJsonFetch('/api/leaderboard/seasons');
        if (!data) return;

        if (data.seasons && data.seasons.length > 0) {
            select.innerHTML = data.seasons
                .map(
                    (s) =>
                        `<option value="${escapeHtml(s.season)}"${s.isCurrent ? ' selected' : ''}>${escapeHtml(
                            s.label
                        )}${s.isCurrent ? ` (${t('leaderboard.currentSeason')})` : ''} - ${s.playerCount} ${t('leaderboard.players')}` + `</option>`
                )
                .join('');
        }
    } catch (err) {
        console.error('[Landing] Failed to load seasons:', err);
    }

    select.addEventListener('change', () => {
        currentSelectedSeason = select.value;
        loadSeasonalLeaderboard(currentLeaderboardCategory);
    });

    const tabs = document.getElementById('leaderboard-tabs');
    if (tabs) {
        tabs.querySelectorAll('.tab-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                tabs.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
                btn.classList.add('active');
                currentLeaderboardCategory = btn.dataset.tab;
                loadSeasonalLeaderboard(currentLeaderboardCategory);
            });
        });
    }
}

/**
 * Loads seasonal leaderboard data for a given category
 * @param {string} category - 'wins' | 'elo' | 'points' | 'games'
 */
export async function loadSeasonalLeaderboard(category) {
    currentLeaderboardCategory = category;

    try {
        const season = currentSelectedSeason || 'current';
        const data = await safeJsonFetch(
            `/api/leaderboard/season/${encodeURIComponent(season)}?category=${category}&limit=10`
        );
        if (!data) return;

        const badge = document.getElementById('season-badge');
        if (badge && data.seasonLabel) {
            badge.textContent = t('leaderboard.seasonLabel', { label: data.seasonLabel });
            badge.style.display = 'block';
        }

        const container = document.getElementById('leaderboard-content');
        if (data.data && data.data.length > 0) {
            // NOTE: All values are server-provided numeric/string data passed through escapeHtml
            container.innerHTML = data.data
                .map((p, i) => {
                    const rankClass = i < 3 ? 'top-3' : '';
                    const rankColor = i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : '';
                    let statsContent;

                    switch (category) {
                        case 'elo':
                            statsContent = `<span class="lb-elo">${p.elo || 1000}</span>`;
                            break;
                        case 'points':
                            statsContent = `<span class="lb-points">${p.points || 0}</span>`;
                            break;
                        case 'games':
                            statsContent = `<span class="lb-games">${p.gamesPlayed || 0}</span>`;
                            break;
                        default:
                            statsContent = `<span class="lb-wins">${p.wins}${t('leaderboard.winsShort')}</span><span class="lb-winrate">${p.winrate}%</span>`;
                    }

                    return (
                        `<div class="leaderboard-row ${rankClass}">` +
                        `<span class="lb-rank ${rankColor}">#${p.rank}</span>` +
                        `<span class="lb-name">${escapeHtml(p.nickname)}</span>` +
                        `<span class="lb-stats">${statsContent}</span>` +
                        `</div>`
                    );
                })
                .join('');
        } else {
            container.innerHTML = `<div class="leaderboard-empty">${t('leaderboard.noPlayersThisSeason')}</div>`;
        }
    } catch (err) {
        console.error('[Landing] Failed to load seasonal leaderboard:', err);
    }
}

/**
 * Initializes extended leaderboard cards with IntersectionObserver
 */
export function initExtendedLeaderboards() {
    if (extendedLeaderboardsInitialized) return;
    extendedLeaderboardsInitialized = true;

    leaderboardObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    const card = entry.target;
                    if (!card.dataset.loaded) {
                        loadLeaderboardCard(card);
                        card.dataset.loaded = 'true';
                    }
                }
            });
        },
        { threshold: 0.1, rootMargin: '50px' }
    );

    document.querySelectorAll('.lb-card').forEach((card) => {
        leaderboardObserver.observe(card);
    });

    loadGlobalLeaderboards();
}

/**
 * Loads all global leaderboard categories
 */
export async function loadGlobalLeaderboards() {
    const categories = ['wins', 'winrate', 'games', 'elo'];
    for (const cat of categories) {
        await loadGlobalLeaderboardCard(cat);
    }
}

/**
 * Loads a single global leaderboard card
 * @param {string} category
 */
export async function loadGlobalLeaderboardCard(category) {
    const container = document.getElementById(`lb-global-${category}`);
    if (!container) return;

    try {
        const data = await safeJsonFetch(`/api/leaderboard?category=${category}&limit=5`);
        if (!data) {
            const card = container.closest('.leaderboard-card');
            if (card) card.style.display = 'none';
            return;
        }

        if (data.data && data.data.length > 0) {
            // NOTE: All user-provided strings passed through escapeHtml, numeric values are integers
            container.innerHTML = data.data
                .map((p, i) => {
                    const topClass = i === 0 ? 'top-1' : i === 1 ? 'top-2' : i === 2 ? 'top-3' : '';
                    let valueDisplay = '';
                    switch (category) {
                        case 'wins':
                            valueDisplay = `${p.wins}`;
                            break;
                        case 'winrate':
                            valueDisplay = `${p.winrate}%`;
                            break;
                        case 'games':
                            valueDisplay = `${p.gamesPlayed}`;
                            break;
                        case 'elo':
                            valueDisplay = `${p.elo || 1000}`;
                            break;
                    }
                    return (
                        `<div class="lb-entry ${topClass}">` +
                        `<span class="lb-entry-rank">#${p.rank}</span>` +
                        `<span class="lb-entry-name">${escapeHtml(p.nickname)}</span>` +
                        `<span class="lb-entry-value">${valueDisplay}</span>` +
                        `</div>`
                    );
                })
                .join('');
        } else {
            container.innerHTML =
                `<div class="lb-empty"><span class="lb-empty-icon">&#x1F3C6;</span><span>${t('leaderboard.noData')}</span></div>`;
        }
    } catch (err) {
        console.error(`[Leaderboard] Failed to load global ${category}:`, err);
        container.innerHTML = `<div class="lb-empty">${t('leaderboard.loadError')}</div>`;
    }
}

/**
 * Loads a map-specific leaderboard card
 * @param {HTMLElement} card
 */
export async function loadLeaderboardCard(card) {
    const mapSize = card.closest('.leaderboard-group')?.dataset.map;
    const category = card.dataset.category;
    if (!mapSize || mapSize === 'global' || !category) return;

    const containerId = `lb-${mapSize}-${category}`;
    const container = document.getElementById(containerId);
    if (!container) return;

    try {
        const data = await safeJsonFetch(`/api/leaderboard/map/${mapSize}/${category}?limit=5`);
        if (!data) return;

        if (data.data && data.data.length > 0) {
            // NOTE: All user-provided strings passed through escapeHtml, numeric values are integers
            container.innerHTML = data.data
                .map((p, i) => {
                    const topClass = i === 0 ? 'top-1' : i === 1 ? 'top-2' : i === 2 ? 'top-3' : '';
                    const valueFormatted = formatLeaderboardValue(p.value, category);
                    return (
                        `<div class="lb-entry ${topClass}">` +
                        `<span class="lb-entry-rank">#${p.rank}</span>` +
                        `<span class="lb-entry-name">${escapeHtml(p.nickname)}</span>` +
                        `<span class="lb-entry-value">${valueFormatted}</span>` +
                        `<span class="lb-entry-games">${p.games} ${t('leaderboard.gamesShort')}</span>` +
                        `</div>`
                    );
                })
                .join('');
        } else {
            container.innerHTML =
                `<div class="lb-empty"><span class="lb-empty-icon">&#x1F3C6;</span><span>${t('leaderboard.noData')}</span></div>`;
        }
    } catch (err) {
        console.error(`[Leaderboard] Failed to load map ${mapSize} ${category}:`, err);
        container.innerHTML = `<div class="lb-empty">${t('leaderboard.loadError')}</div>`;
    }
}

/**
 * Formats a leaderboard value for display
 * @param {*} value
 * @param {string} category
 * @returns {string}
 */
export function formatLeaderboardValue(value, category) {
    value = parseInt(value, 10) || 0;
    switch (category) {
        case 'highest_points':
        case 'total_points':
            return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString();
        default:
            return value.toString();
    }
}
